import type { InstrumentRunAssayVersion } from '@/types';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import Table from '@nautilusbio/nb-design-system/Table';
import { Text } from '@nautilusbio/nb-design-system/Text';
import type { ColDef } from 'ag-grid-enterprise';
import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { getControlRows, type ControlRow } from './controlRows';

type Props = {
  assayVersion: InstrumentRunAssayVersion;
  numSamples: number;
  useTauLibPrepControl2?: boolean;
  /** Actual flowcell SNs from run.consumables.three_flow_cell_ids (split by comma).
   *  When provided, replaces the "Flowcell N" label with the actual SN. */
  flowcellSns?: string[];
};

const ControlsSection = ({ assayVersion, numSamples, useTauLibPrepControl2 = false, flowcellSns }: Props) => {
  const { t } = useTranslation('runs', { keyPrefix: 'controls' });

  const rowData = useMemo(
    () => getControlRows(assayVersion, numSamples, useTauLibPrepControl2),
    [assayVersion, numSamples, useTauLibPrepControl2],
  );

  const colDefs = useMemo<ColDef<ControlRow>[]>(
    () => [
      {
        field: 'flow_cell',
        headerName: t('flowcell'),
        editable: false,
        valueFormatter: params => {
          const fcNum = params.value as number | undefined;
          if (fcNum === undefined || fcNum === null) return '—';
          if (flowcellSns && flowcellSns[fcNum - 1]) return flowcellSns[fcNum - 1];
          return `Flowcell ${fcNum}`;
        },
        width: 120,
      },
      {
        field: 'lane',
        headerName: t('lane'),
        editable: false,
        width: 70,
      },
      {
        field: 'well_position',
        headerName: t('well'),
        editable: false,
        width: 70,
      },
      {
        field: 'demux_index',
        headerName: t('demux_id'),
        editable: false,
        width: 90,
      },
      {
        field: 'name',
        headerName: t('sample_name'),
        editable: false,
        flex: 1,
      },
      {
        field: 'control_type',
        headerName: t('type'),
        editable: false,
        flex: 1,
      },
    ],
    [t, flowcellSns],
  );

  if (rowData.length === 0) return null;

  return (
    <Stack fullWidth gap={2} orientation="vertical">
      <Text as="h3">{t('title')}</Text>
      <Table
        columnDefs={colDefs}
        domLayout="autoHeight"
        enableAdvancedFilter={false}
        rowData={rowData}
        suppressContextMenu
        tableId="controlsSection"
        enableResponsiveResize={false}
      />
    </Stack>
  );
};

export default memo(ControlsSection);
